/**
 * Invoice History — the per-submission invoice table (OR-1449).
 *
 * Eleven columns and a pinned actions column, so it scrolls horizontally.
 * Sorting is client-side and the search filters in memory: the backend's
 * list endpoint takes only offset/limit, and volume is low enough by
 * design that paging never kicks in.
 *
 * Archived rows sink to the bottom, struck through and dimmed. Nothing can
 * reach that state yet — the backend has no archive endpoint — but the
 * ordering and styling live here so wiring it later is one predicate.
 */
import { useMemo, useState, type ReactNode } from "react";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpRight,
    ChevronSelectorVertical,
    Download01,
    Edit05,
    File04,
    Folder,
    RefreshCcw01,
} from "@untitledui/icons";
import { MenuItem as AriaMenuItem, Separator as AriaSeparator } from "react-aria-components";
import { Badge, Table, cx } from "@tricura/atlas-ui";
import { StickyColDivider } from "../../../components/sticky-col-divider";
import { ROW_ACTIONS_ITEM_CLASS, RowActionsMenu } from "../../../components/row-actions-menu";
import { COVERAGE_TYPE_LABELS, type AscendInvoice } from "../../../api";
import { invoiceBadge } from "./invoice-status";

export type InvoiceRowAction = "view" | "edit" | "open-in-ascend" | "archive" | "restore" | "download";

type SortDir = "asc" | "desc";

/** Column widths, summed into the sticky offsets below. */
const W = {
    invoiceNumber: 140,
    namedInsured: 220,
    status: 150,
    dueDate: 120,
    dueValue: 130,
    effective: 120,
    expiration: 120,
    coverageType: 170,
    brokerage: 180,
    brokerName: 160,
    selectedPremium: 150,
    actions: 64,
} as const;

const fixed = (px: number) => ({ width: px, minWidth: px });

const STICKY_RIGHT_HEAD =
    "md:sticky md:right-0 md:z-10 md:bg-secondary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)] max-md:static!";
const STICKY_RIGHT_CELL =
    "md:sticky md:right-0 md:z-10 md:bg-primary md:shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)] max-md:static!";

/**
 * Archived rows keep their history visible but drop out of the active
 * reading order. `archivedAt` is the backend's `invoice_archived_at`,
 * which the archive DELETE sets (backend PR #383).
 */
function isArchived(invoice: AscendInvoice): boolean {
    return invoice.archivedAt != null;
}

interface Column {
    id: string;
    label: string;
    sortable: boolean;
    width: number;
    /** Right-align the numeric columns. */
    numeric?: boolean;
}

const COLUMNS: readonly Column[] = [
    { id: "invoiceNumber", label: "Invoice Number", sortable: true, width: W.invoiceNumber },
    { id: "namedInsured", label: "Named Insured", sortable: true, width: W.namedInsured },
    { id: "status", label: "Status", sortable: false, width: W.status },
    { id: "dueDate", label: "Due date", sortable: true, width: W.dueDate },
    { id: "dueValue", label: "Due Value", sortable: false, width: W.dueValue, numeric: true },
    { id: "effective", label: "Effective", sortable: true, width: W.effective },
    { id: "expiration", label: "Expiration", sortable: true, width: W.expiration },
    { id: "coverageType", label: "Coverage type", sortable: true, width: W.coverageType },
    { id: "brokerage", label: "Brokerage", sortable: true, width: W.brokerage },
    { id: "brokerName", label: "Broker Name", sortable: true, width: W.brokerName },
    { id: "selectedPremium", label: "Selected Premium", sortable: false, width: W.selectedPremium, numeric: true },
] as const;

/** Without this the `table-fixed` layout shrinks columns to fit instead of
 *  overflowing, and the horizontal scroll the design calls for never
 *  appears. Same trick as the Triage table. */
const TOTAL_WIDTH = COLUMNS.reduce((sum, c) => sum + c.width, 0) + W.actions;

/**
 * Edge-to-edge rule inside the ⋮ menu.
 *
 * The shared `ROW_ACTIONS_SEPARATOR_CLASS` insets itself with `mx-2.5`,
 * which is what the facility and claim menus use. The invoice menu draws
 * it full-bleed instead, so `-mx-1` cancels the menu's own `p-1` and the
 * line reaches both borders.
 */
const MENU_SEPARATOR_CLASS = "bg-border-secondary -mx-1 my-1 h-px";

function Placeholder() {
    return <span className="text-tertiary text-sm">—</span>;
}

/** `YYYY-MM-DD` → `MM/DD/YYYY`, without going through Date (no TZ shifts). */
function formatDate(iso: string | null): string {
    if (!iso) return "";
    const [y, m, d] = iso.slice(0, 10).split("-");
    return y && m && d ? `${m}/${d}/${y}` : "";
}

function formatMoney(value: number | null): string {
    if (value == null) return "";
    return value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/** The value a column sorts and searches on. */
function sortValue(invoice: AscendInvoice, columnId: string): string | number {
    switch (columnId) {
        case "invoiceNumber":
            return invoice.invoiceNumber;
        case "namedInsured":
            return invoice.namedInsured;
        case "dueDate":
            return invoice.dueDate ?? "";
        case "dueValue":
            return invoice.dueValue ?? 0;
        case "effective":
            return invoice.effectiveDate ?? "";
        case "expiration":
            return invoice.expirationDate ?? "";
        case "coverageType":
            return COVERAGE_TYPE_LABELS[invoice.coverageType] ?? "";
        case "brokerage":
            return invoice.brokerageName;
        case "brokerName":
            return invoice.brokerName;
        case "selectedPremium":
            return invoice.selectedPremium ?? 0;
        default:
            return "";
    }
}

/** Every text a row can be matched on by the search box. */
function searchHaystack(invoice: AscendInvoice): string {
    return [
        invoice.invoiceNumber,
        invoice.namedInsured,
        invoiceBadge(invoice.ascendInvoiceStatus)?.label ?? "",
        formatDate(invoice.dueDate),
        formatDate(invoice.effectiveDate),
        formatDate(invoice.expirationDate),
        COVERAGE_TYPE_LABELS[invoice.coverageType] ?? "",
        invoice.brokerageName,
        invoice.brokerName,
    ]
        .join(" ")
        .toLowerCase();
}

function SortHeader({ label, dir, onToggle }: { label: string; dir: SortDir | null; onToggle: () => void }) {
    const Icon = dir ? (dir === "asc" ? ArrowUp : ArrowDown) : ChevronSelectorVertical;
    const ariaSort = !dir ? "none" : dir === "asc" ? "ascending" : "descending";
    return (
        <button
            type="button"
            onClick={onToggle}
            aria-sort={ariaSort}
            className="text-quaternary focus-visible:ring-focus-ring inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold whitespace-nowrap outline-none focus-visible:rounded focus-visible:ring-2"
        >
            {label}
            <Icon
                aria-hidden="true"
                className={cx("size-3.5 shrink-0", dir ? "text-fg-secondary" : "text-fg-quaternary")}
            />
        </button>
    );
}

function StatusCell({ invoice }: { invoice: AscendInvoice }) {
    if (invoice.syncStatus === "draft") {
        return (
            <Badge color="gray" type="pill-color" size="sm">
                Draft
            </Badge>
        );
    }
    if (isArchived(invoice)) {
        return (
            <Badge color="gray" type="pill-color" size="sm">
                Archived
            </Badge>
        );
    }
    const badge = invoiceBadge(invoice.ascendInvoiceStatus);
    // No badge means the Ascend sync hasn't reported a status yet (or
    // failed). The approved set has no label for that window, so we show
    // the table's usual unknown placeholder rather than inventing one.
    if (!badge) return <Placeholder />;
    return (
        <Badge color={badge.color} type="pill-color" size="sm">
            {badge.label}
        </Badge>
    );
}

/**
 * Single-line menu item. The invoice menu is one line per action in the
 * Figma, unlike the two-line facility/claim menus, so it doesn't reuse
 * `RowActionsMenuItemBody`.
 */
function ActionItem({
    id,
    icon: Icon,
    label,
    isDisabled,
    autotest,
}: {
    id: InvoiceRowAction;
    icon: typeof File04;
    label: string;
    isDisabled?: boolean;
    autotest?: string;
}) {
    return (
        <AriaMenuItem id={id} textValue={label} isDisabled={isDisabled} className={ROW_ACTIONS_ITEM_CLASS} data-autotest={autotest}>
            <span className="text-secondary flex items-center gap-2 text-sm font-semibold">
                {/* The Figma exports these at stroke-width 2, heavier than
                  * Untitled UI's 1.67 default — that's what makes the glyphs
                  * read as solid next to the semibold labels. */}
                <Icon aria-hidden="true" className="text-fg-quaternary size-4 shrink-0 stroke-2" />
                {label}
            </span>
        </AriaMenuItem>
    );
}

function RowActions({
    invoice,
    onAction,
    archiveEnabled,
}: {
    invoice: AscendInvoice;
    onAction: (a: InvoiceRowAction, i: AscendInvoice) => void;
    archiveEnabled: boolean;
}) {
    const archived = isArchived(invoice);
    // A draft has never been sent, so editing it is the only thing that
    // makes sense; every other status is a document already out in the
    // world, where View replaces Edit. Drafts are sync_status rows, not an
    // Ascend billing status — the backend stores the saved form that way.
    const isDraft = invoice.syncStatus === "draft";
    const label = `Actions for invoice ${invoice.invoiceNumber}`;
    const autotest = `invoice-row-actions-${invoice.id}`;

    if (isDraft) {
        return (
            <RowActionsMenu label={label} data-autotest={autotest} onAction={(k) => onAction(k as InvoiceRowAction, invoice)}>
                {/* Opens the form pre-filled and editable. Persisting the
                  * edit still needs a PATCH the backend doesn't have. */}
                <ActionItem id="edit" icon={Edit05} label="Edit" autotest={`${autotest}-edit`} />
            </RowActionsMenu>
        );
    }

    return (
        <RowActionsMenu label={label} data-autotest={autotest} onAction={(k) => onAction(k as InvoiceRowAction, invoice)}>
            <ActionItem id="view" icon={File04} label="View" autotest={`${autotest}-view`} />
            {/* The deep link only exists once the sync created a Program. */}
            <ActionItem
                id="open-in-ascend"
                icon={ArrowUpRight}
                label="Open in Ascend"
                isDisabled={!invoice.ascendProgramUrl}
                autotest={`${autotest}-open-in-ascend`}
            />
            {archived ? (
                /* TODO(OR-1449): the backend archives but cannot un-archive
                 * — no endpoint exists, so Restore has nothing to call. */
                <ActionItem
                    id="restore"
                    icon={RefreshCcw01}
                    label="Restore"
                    isDisabled
                    autotest={`${autotest}-restore`}
                />
            ) : (
                <ActionItem
                    id="archive"
                    icon={Folder}
                    label="Archive"
                    isDisabled={!archiveEnabled}
                    autotest={`${autotest}-archive`}
                />
            )}
            {/* Download sits below a rule: it acts on the document, not on
              * the invoice record like the three above it. */}
            <AriaSeparator className={MENU_SEPARATOR_CLASS} />
            <ActionItem id="download" icon={Download01} label="Download" autotest={`${autotest}-download`} />
        </RowActionsMenu>
    );
}

/**
 * The search predicate, exported so the page can tell "no invoices yet"
 * from "the search hid them all" without duplicating the rule. The two
 * cases render differently per the Figma: the first keeps the column
 * headers visible, the second replaces the whole table.
 */
export function filterInvoices(invoices: readonly AscendInvoice[], search: string): AscendInvoice[] {
    const needle = search.trim().toLowerCase();
    return needle ? invoices.filter((i) => searchHaystack(i).includes(needle)) : [...invoices];
}

interface InvoiceHistoryTableProps {
    invoices: readonly AscendInvoice[];
    /** Free-text filter from the card's search box. */
    search: string;
    onAction: (action: InvoiceRowAction, invoice: AscendInvoice) => void;
    /** Off only where archiving makes no sense (e.g. the synthesized draft
     *  row, which has no invoice to DELETE). Restore stays disabled
     *  regardless: the backend cannot un-archive. */
    archiveEnabled?: boolean;
    /**
     * Shown centred over the (empty) table body, so the column headers
     * stay on screen — the Figma's empty state keeps them.
     */
    emptyOverlay?: ReactNode;
}

export function InvoiceHistoryTable({ invoices, search, onAction, archiveEnabled = false, emptyOverlay }: InvoiceHistoryTableProps) {
    const [sortColumn, setSortColumn] = useState<string | null>(null);
    const [sortDir, setSortDir] = useState<SortDir | null>(null);

    /** none → asc → desc → none, resetting when a different column is hit. */
    function cycleSort(columnId: string) {
        if (sortColumn !== columnId) {
            setSortColumn(columnId);
            setSortDir("asc");
            return;
        }
        if (sortDir === "asc") {
            setSortDir("desc");
            return;
        }
        setSortColumn(null);
        setSortDir(null);
    }

    const rows = useMemo(() => {
        const filtered = filterInvoices(invoices, search);

        if (sortColumn && sortDir) {
            filtered.sort((a, b) => {
                const av = sortValue(a, sortColumn);
                const bv = sortValue(b, sortColumn);
                const cmp =
                    typeof av === "number" && typeof bv === "number"
                        ? av - bv
                        : String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
                return sortDir === "asc" ? cmp : -cmp;
            });
        }

        // Archived always sinks, whatever the sort — it's history, not a
        // peer of the live rows.
        return filtered.sort((a, b) => Number(isArchived(a)) - Number(isArchived(b)));
    }, [invoices, search, sortColumn, sortDir]);

    function renderCell(columnId: string, invoice: AscendInvoice) {
        switch (columnId) {
            case "invoiceNumber":
                return <span className="text-primary text-sm font-medium">{invoice.invoiceNumber}</span>;
            case "namedInsured":
                return invoice.namedInsured ? (
                    <span className="text-secondary text-sm">{invoice.namedInsured}</span>
                ) : (
                    <Placeholder />
                );
            case "status":
                return <StatusCell invoice={invoice} />;
            case "dueDate":
                return formatDate(invoice.dueDate) ? (
                    <span className="text-secondary text-sm tabular-nums">{formatDate(invoice.dueDate)}</span>
                ) : (
                    <Placeholder />
                );
            case "dueValue":
                return invoice.dueValue == null ? (
                    <Placeholder />
                ) : (
                    <span className="text-secondary text-sm tabular-nums">{formatMoney(invoice.dueValue)}</span>
                );
            case "effective":
                return formatDate(invoice.effectiveDate) ? (
                    <span className="text-secondary text-sm tabular-nums">{formatDate(invoice.effectiveDate)}</span>
                ) : (
                    <Placeholder />
                );
            case "expiration":
                return formatDate(invoice.expirationDate) ? (
                    <span className="text-secondary text-sm tabular-nums">{formatDate(invoice.expirationDate)}</span>
                ) : (
                    <Placeholder />
                );
            case "coverageType":
                return COVERAGE_TYPE_LABELS[invoice.coverageType] ? (
                    <span className="text-secondary text-sm">{COVERAGE_TYPE_LABELS[invoice.coverageType]}</span>
                ) : (
                    <Placeholder />
                );
            case "brokerage":
                return invoice.brokerageName ? (
                    <span className="text-secondary text-sm">{invoice.brokerageName}</span>
                ) : (
                    <Placeholder />
                );
            case "brokerName":
                return invoice.brokerName ? (
                    <span className="text-secondary text-sm">{invoice.brokerName}</span>
                ) : (
                    <Placeholder />
                );
            case "selectedPremium":
                return invoice.selectedPremium == null ? (
                    <Placeholder />
                ) : (
                    <span className="text-secondary text-sm tabular-nums">{formatMoney(invoice.selectedPremium)}</span>
                );
            default:
                return null;
        }
    }

    return (
        <div className={cx("relative", rows.length === 0 && "min-h-80")}>
            <Table
            aria-label="Invoice History"
            data-autotest="invoice-history-table"
            className="table-fixed"
            wrapperClassName="atlas-scrollbar overflow-x-auto"
            style={{ minWidth: TOTAL_WIDTH }}
        >
            <Table.Header className="bg-secondary">
                {COLUMNS.map((c) => (
                    <Table.Head
                        key={c.id}
                        id={c.id}
                        // react-aria refuses to build the collection without
                        // exactly this: one column marked as the row's header.
                        // The invoice number is what identifies a row.
                        isRowHeader={c.id === "invoiceNumber"}
                        style={fixed(c.width)}
                        className={cx(c.numeric && "[&>*]:w-full [&>*]:justify-end")}
                        label={c.sortable ? undefined : c.label}
                        data-autotest={`invoice-history-header-${c.id}`}
                    >
                        {c.sortable ? (
                            <SortHeader
                                label={c.label}
                                dir={sortColumn === c.id ? sortDir : null}
                                onToggle={() => cycleSort(c.id)}
                            />
                        ) : undefined}
                    </Table.Head>
                ))}
                {/* No visible label: "Actions" in the Figma is an annotation
                  * tag, not a column header — the same blue chip used for
                  * "Warning message" and "Interaction". The header cell stays
                  * pinned all the same, empty, so the data columns scroll
                  * underneath the frozen ⋮ instead of surfacing above it.
                  * `textValue` keeps the column named for screen readers. */}
                <Table.Head
                    id="actions"
                    textValue="Actions"
                    aria-label="Actions"
                    style={fixed(W.actions)}
                    className={cx(STICKY_RIGHT_HEAD, "pr-6")}
                />
            </Table.Header>
            <Table.Body items={rows}>
                {(invoice: AscendInvoice) => (
                    <Table.Row
                        id={invoice.id}
                        data-autotest="invoice-history-row"
                        className={cx(isArchived(invoice) && "text-quaternary opacity-60 line-through")}
                    >
                        {[
                            ...COLUMNS.map((c) => (
                                <Table.Cell
                                    key={c.id}
                                    style={fixed(c.width)}
                                    className={cx(c.numeric && "text-right")}
                                    data-autotest={`invoice-history-cell-${c.id}`}
                                >
                                    {renderCell(c.id, invoice)}
                                </Table.Cell>
                            )),
                            <Table.Cell key="actions" style={fixed(W.actions)} className={cx(STICKY_RIGHT_CELL, "pr-6")}>
                                <div className="flex justify-end">
                                    <RowActions invoice={invoice} onAction={onAction} archiveEnabled={archiveEnabled} />
                                </div>
                                <StickyColDivider mdOnly side="left" />
                            </Table.Cell>,
                        ]}
                    </Table.Row>
                )}
            </Table.Body>
            </Table>

            {rows.length === 0 && emptyOverlay && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                    <div className="pointer-events-auto">{emptyOverlay}</div>
                </div>
            )}
        </div>
    );
}
